import { useEffect, useState } from 'react';
import { confirm } from '../utils/confirm';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../components/Toast';
import { useModal } from '../components/ModalProvider';
import * as tasksApi from '../api/tasks';
import { listGroups } from '../api/groups';
import { myStaff } from '../api/users';
import { fmtDate, fmtDateTime, fmtVND, taskCompletion, isLate } from '../utils/format';
import { StatusChip } from '../components/Chips';

// Khóa định danh item: việc quét -> custId; việc đơn hàng -> custCode.
function itemKey(it) {
  return it.custId ? it.custId._id : it.custCode;
}
// Thông tin KH để hiển thị (việc quét có Customer; việc đơn hàng dùng ảnh chụp).
function itemCust(it) {
  if (it.custId) return it.custId;
  return { code: it.custCode, name: it.custName, sale: it.custSale, ratio: null, debt: null };
}

// Thanh tiến độ 2 màu: phần xanh = đã hoàn thành, phần đỏ = chưa hoàn thành.
// Thanh tiến độ theo MÀU NGỮ NGHĨA (hệ thiết kế mới): track xám, fill đổi màu theo ngưỡng
// tiến độ công việc — ≥50% xanh, 20–49% hổ phách, <20% đỏ (thay kiểu nền đỏ toàn bộ cũ).
function TwoColorBar({ pct, style }) {
  const p = Math.max(0, Math.min(100, Math.round(pct || 0)));
  const color = p >= 50 ? 'var(--ok)' : p >= 20 ? 'var(--warn-fill)' : 'var(--danger)';
  return (
    <div style={{ height: 8, background: 'var(--track)', borderRadius: 5, overflow: 'hidden', ...style }}>
      <div style={{ width: `${p}%`, height: '100%', background: color, borderRadius: 5 }}></div>
    </div>
  );
}

export default function TasksPage() {
  const { me } = useAuth();
  const toast = useToast();
  const { openModal, closeModal } = useModal();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);

  function reload() {
    setLoading(true);
    return tasksApi
      .listTasks()
      .then(setTasks)
      .finally(() => setLoading(false));
  }

  useEffect(() => {
    reload();
  }, []);

  function roleSubtitle() {
    if (me.role === 'GD') return 'Toàn bộ công việc các phòng';
    if (me.role === 'TP') return 'Công việc của nhóm bạn — nhận & giao cho nhân viên';
    return 'Công việc được giao cho bạn';
  }

  async function openCreateTask() {
    const groups = await listGroups();
    openModal(
      <CreateTaskForm
        groups={groups}
        onCancel={closeModal}
        onSubmit={async () => {
          toast('Hãy tạo công việc từ màn Danh sách quét', 'warn');
          closeModal();
        }}
      />
    );
  }

  function openDetail(taskId, onBack) {
    openModal(
      <TaskDetailModal
        taskId={taskId}
        onClose={closeModal}
        onBack={onBack}
        onChanged={async () => {
          await reload();
        }}
      />,
      { wide: true }
    );
  }

  // Chi tiết 1 "đầu việc" gồm nhiều nhân viên (theo batchKey): bảng theo từng nhân viên.
  // Bấm 1 NV -> mở chi tiết KH, kèm nút "Quay lại" để trở về bảng này.
  function openGroupDetail(subTasks) {
    openModal(
      <GroupDetailModal
        tasks={subTasks}
        onOpenTask={(taskId) => openDetail(taskId, () => openGroupDetail(subTasks))}
        onClose={closeModal}
        onChanged={reload}
      />,
      { wide: true }
    );
  }

  // GĐ xóa công việc ngay từ thẻ ở màn chính (xóa cả các đầu việc cùng batchKey).
  async function deleteTasks(ids, label) {
    if (!await confirm(`Xóa công việc "${label}"? Hành động này không thể hoàn tác.`)) return;
    try {
      for (const id of ids) await tasksApi.deleteTask(id);
      toast('Đã xóa công việc', 'warn');
      await reload();
    } catch (err) {
      toast(err.response?.data?.message || 'Có lỗi xảy ra', 'danger');
    }
  }
  // GĐ xóa mọi việc; TP chỉ xóa việc do CHÍNH MÌNH tạo (không xóa việc của Giám đốc).
  const myId = String(me.id || me._id);
  const canDeleteTask = (t) =>
    me.role === 'GD' || (me.role === 'TP' && String(t?.createdBy?._id || t?.createdBy || '') === myId);

  return (
    <>
      <div className="page-head">
        <div>
          <h2>Quản lý công việc</h2>
          <div className="sub">
            {roleSubtitle()}
            {!loading && tasks.length ? ` · ${new Set(tasks.map((t) => t.batchKey || t._id)).size} nhiệm vụ đang chạy` : ''}
          </div>
        </div>
        {(me.role === 'GD' || me.role === 'TP') && (
          <button className="btn" onClick={openCreateTask}>+ Giao việc mới</button>
        )}
      </div>

      {loading ? (
        <div className="card">
          <div className="empty">Đang tải...</div>
        </div>
      ) : !tasks.length ? (
        <div className="card">
          <div className="empty">Chưa có công việc nào.</div>
        </div>
      ) : (
        (() => {
          // Gộp các đầu việc cùng batchKey (tạo cùng 1 lần, chia theo NV) -> 1 ô.
          const groupsMap = new Map();
          const singles = [];
          tasks.forEach((t) => {
            if (t.batchKey) {
              if (!groupsMap.has(t.batchKey)) groupsMap.set(t.batchKey, []);
              groupsMap.get(t.batchKey).push(t);
            } else {
              singles.push(t);
            }
          });
          return (
            <>
              {[...groupsMap.entries()].map(([key, subTasks]) => (
                <OrderTaskGroupCard
                  key={key}
                  tasks={subTasks}
                  onOpenGroup={openGroupDetail}
                  canDelete={canDeleteTask(subTasks[0])}
                  onDelete={() => deleteTasks(subTasks.map((t) => t._id), subTasks[0].title)}
                />
              ))}
              {singles.map((t) => (
                <TaskCard
                  key={t._id}
                  task={t}
                  onOpen={() => openDetail(t._id)}
                  canDelete={canDeleteTask(t)}
                  onDelete={() => deleteTasks([t._id], t.title)}
                />
              ))}
            </>
          );
        })()
      )}
    </>
  );
}

// Thẻ tóm tắt 1 "đầu việc" giao cho nhiều nhân viên/nhóm. Chi tiết theo từng NV xem ở màn chi tiết.
function OrderTaskGroupCard({ tasks, onOpenGroup, canDelete, onDelete }) {
  const head = tasks[0];
  let done = 0;
  let total = 0;
  tasks.forEach((t) => {
    const c = taskCompletion(t);
    done += c.done;
    total += c.total;
  });
  const pct = total ? Math.round((done / total) * 100) : 0;
  const assignedCount = tasks.filter((t) => t.assignTo).length;

  return (
    <div className="card task-row" onClick={() => onOpenGroup(tasks)}>
      <div className="task-row-main">
        <div className="task-row-title">
          {head.title}
          <span className="chip doing" style={{ marginLeft: 8 }}>{tasks.length} nhóm/NV</span>
        </div>
        {head.detail && <div className="small muted task-row-detail">{head.detail}</div>}
        <div className="small muted">
          Đã giao {assignedCount}/{tasks.length} · Hạn NV {fmtDate(head.deadline)} · Hạn duyệt {fmtDate(head.reviewDeadline)}
        </div>
        {head.createdBy && (
          <div className="small muted">
            Tạo bởi <b>{head.createdBy.name}</b>
            {head.createdAt ? ` · ${fmtDate(head.createdAt)}` : ''}
          </div>
        )}
      </div>

      <div className="task-row-prog">
        <div className="row between small" style={{ marginBottom: 3 }}>
          <span className="muted">{done}/{total} KH đạt</span>
          <span className="mono" style={{ fontWeight: 700, color: pct >= 50 ? 'var(--ok)' : pct >= 20 ? 'var(--warn)' : 'var(--danger)' }}>{pct}%</span>
        </div>
        <TwoColorBar pct={pct} />
      </div>

      <div className="task-row-act">
        <button className="btn sm ghost" onClick={(e) => { e.stopPropagation(); onOpenGroup(tasks); }}>
          Chi tiết →
        </button>
        {canDelete && (
          <button className="btn sm danger" onClick={(e) => { e.stopPropagation(); onDelete(); }}>
            🗑
          </button>
        )}
      </div>
    </div>
  );
}

// Màn chi tiết 1 đầu việc nhiều nhân viên: bảng theo từng nhân viên, bấm để xem KH của NV đó.
function GroupDetailModal({ tasks, onOpenTask, onClose, onChanged }) {
  const { me } = useAuth();
  const toast = useToast();
  const [rows, setRows] = useState(tasks); // bản cục bộ để làm mới sau khi giao việc
  const head = rows[0];
  let done = 0;
  let total = 0;
  rows.forEach((t) => {
    const c = taskCompletion(t);
    done += c.done;
    total += c.total;
  });
  const pct = total ? Math.round((done / total) * 100) : 0;

  // TP của nhóm: có thể "giao việc" hàng loạt cho các đầu việc đang chờ xác nhận.
  const isTPofGroup = me.role === 'TP' && String(head.group?._id) === String(me.group?.id);
  const pendingTasks = rows.filter((t) => t.status === 'pending_confirm');

  // Tải lại trạng thái mới của các đầu việc (giữ nguyên modal).
  async function refreshRows() {
    const updated = await Promise.all(rows.map((t) => tasksApi.getTask(t._id).catch(() => t)));
    setRows(updated);
  }

  async function confirmAll() {
    if (!pendingTasks.length) return;
    try {
      for (const t of pendingTasks) await tasksApi.confirmTask(t._id);
      toast(`Đã giao việc cho ${pendingTasks.length} nhân viên`);
      await refreshRows(); // cập nhật ngay trên màn này, KHÔNG đóng
      await onChanged(); // đồng bộ lại bảng nền
    } catch (err) {
      toast(err.response?.data?.message || 'Có lỗi xảy ra', 'danger');
    }
  }

  return (
    <>
      <span className="close" onClick={onClose}>
        ×
      </span>
      <h3>{head.title}</h3>
      <div className="small muted" style={{ marginBottom: 10 }}>
        {head.detail}
      </div>
      <div className="row between small" style={{ marginBottom: 4 }}>
        <span className="muted">
          Tiến độ chung {done}/{total} KH đạt
        </span>
        <span style={{ fontWeight: 700 }}>{pct}%</span>
      </div>
      <TwoColorBar pct={pct} style={{ marginBottom: 14 }} />

      <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 8 }}>Chi tiết theo từng nhân viên ({rows.length})</div>
      <table>
        <thead>
          <tr>
            <th>Nhân viên phụ trách</th>
            <th>Nhóm</th>
            <th className="c">Số KH</th>
            <th className="c">Tiến độ</th>
            <th className="c">Trạng thái</th>
            <th className="c"></th>
          </tr>
        </thead>
        <tbody>
          {rows.map((t) => {
            const c = taskCompletion(t);
            return (
              <tr key={t._id}>
                <td>{t.assignTo ? t.assignTo.name : <span className="muted">Chưa giao</span>}</td>
                <td>{t.group?.name || '—'}</td>
                <td className="c">{c.total}</td>
                <td className="c">
                  {c.done}/{c.total}
                </td>
                <td className="c">
                  <StatusChip task={t} />
                </td>
                <td className="c">
                  <button className="btn sm ghost" onClick={() => onOpenTask(t._id)}>
                    Xem →
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <div className="row" style={{ marginTop: 12 }}>
        {isTPofGroup && pendingTasks.length > 0 && (
          <button className="btn ok" onClick={confirmAll}>
            ✅ Giao việc cho tất cả ({pendingTasks.length} nhân viên)
          </button>
        )}
        <div className="spacer"></div>
        <button className="btn ghost" onClick={onClose}>
          Đóng
        </button>
      </div>
    </>
  );
}

function TaskCard({ task, onOpen, canDelete, onDelete }) {
  const comp = taskCompletion(task);
  const dlStyle = isLate(task) && task.status !== 'done' ? { color: 'var(--danger)', fontWeight: 700 } : {};

  return (
    <div className="card task-row" onClick={onOpen}>
      <div className="task-row-main">
        <div className="task-row-title">
          {task.title}
          <StatusChip task={task} />
        </div>
        {task.detail && <div className="small muted task-row-detail">{task.detail}</div>}
        <div className="small muted">
          {task.group?.name ? `Nhóm ${task.group.name} · ` : ''}
          Phụ trách {task.assignTo ? <b>{task.assignTo.name}</b> : <span className="muted">chưa giao</span>}
          {' · Hạn NV '}<span style={dlStyle}>{fmtDate(task.deadline)}</span>
          {' · Hạn duyệt '}{fmtDate(task.reviewDeadline)}
        </div>
        {task.createdBy && (
          <div className="small muted">
            Tạo bởi <b>{task.createdBy.name}</b>
            {task.createdAt ? ` · ${fmtDate(task.createdAt)}` : ''}
          </div>
        )}
      </div>

      <div className="task-row-prog">
        <div className="row between small" style={{ marginBottom: 3 }}>
          <span className="muted">{comp.done}/{comp.total} KH đạt</span>
          <span className="mono" style={{ fontWeight: 700, color: comp.pct >= 50 ? 'var(--ok)' : comp.pct >= 20 ? 'var(--warn)' : 'var(--danger)' }}>{comp.pct}%</span>
        </div>
        <TwoColorBar pct={comp.pct} />
      </div>

      <div className="task-row-act">
        <button className="btn sm ghost" onClick={(e) => { e.stopPropagation(); onOpen(); }}>
          Chi tiết →
        </button>
        {canDelete && (
          <button className="btn sm danger" onClick={(e) => { e.stopPropagation(); onDelete(); }}>
            🗑
          </button>
        )}
      </div>
    </div>
  );
}

function TaskDetailModal({ taskId, onClose, onBack, onChanged }) {
  const { me } = useAuth();
  const toast = useToast();
  const [task, setTask] = useState(null);
  const [staff, setStaff] = useState([]);
  const [assignOpen, setAssignOpen] = useState(false);
  const [notes, setNotes] = useState({});
  const [picks, setPicks] = useState({}); // trạng thái kết quả NV chọn theo item
  const [reviewNotes, setReviewNotes] = useState({}); // ghi chú TP theo item
  const [replyText, setReplyText] = useState(''); // ô nhập trao đổi

  async function load() {
    const t = await tasksApi.getTask(taskId);
    setTask(t);
    const initial = {};
    const initPicks = {};
    const initReview = {};
    t.items.forEach((it) => {
      initial[itemKey(it)] = it.note || '';
      initPicks[itemKey(it)] = it.resultStatus || '';
      initReview[itemKey(it)] = it.reviewNote || '';
    });
    setNotes(initial);
    setPicks(initPicks);
    setReviewNotes(initReview);
  }

  const [editing, setEditing] = useState(false);

  useEffect(() => {
    load();
  }, [taskId]);

  if (!task) return null;

  if (editing) {
    return (
      <EditTaskForm
        task={task}
        onCancel={() => setEditing(false)}
        onSaved={async () => {
          setEditing(false);
          await load();
          await onChanged();
        }}
      />
    );
  }

  const assignee = task.assignTo;
  const canEdit = me.role === 'GD';
  const canAssign = me.role === 'TP' && String(task.group?._id) === String(me.group?.id);
  const isOwnerNV = me.role === 'NV' && String(task.assignTo?._id) === String(me.id);
  const canReview = me.role === 'TP' && String(task.group?._id) === String(me.group?.id);
  // TP tự care KH (assignTo = chính TP): tự điền kết quả + kết thúc trực tiếp, không cần giao NV.
  const isSelfLeadTask = me.role === 'TP' && String(task.assignTo?._id) === String(me.id) && String(task.group?._id) === String(me.group?.id);
  // Sau khi đã hoàn tất duyệt (done) -> TP không sửa duyệt/ghi chú nữa.
  const canReviewNow = canReview && task.status !== 'done';
  // Ô "Ghi chú TP" + nút duyệt: chỉ cho luồng NV nộp → TP duyệt (ẩn khi TP tự làm KH của mình).
  const showReview = canReviewNow && !isSelfLeadTask;
  const comp = taskCompletion(task);

  async function saveNote(custId) {
    const note = notes[custId];
    if (!note || !note.trim()) return toast('Nhập kết quả trước khi lưu', 'warn');
    const hasStatuses = task.statuses && task.statuses.length;
    if (hasStatuses && !picks[custId]) return toast('Hãy chọn trạng thái kết quả cho KH này', 'warn');
    try {
      await tasksApi.saveItemNote(task._id, custId, note, picks[custId] || '');
      toast('Đã lưu kết quả KH');
      await load();
      await onChanged();
    } catch (err) {
      toast(err.response?.data?.message || 'Có lỗi xảy ra', 'danger');
    }
  }

  async function doSubmit() {
    try {
      await tasksApi.submitTask(task._id);
      toast('Đã nộp, chờ trưởng phòng duyệt');
      await load();
      await onChanged();
    } catch (err) {
      toast(err.response?.data?.message || 'Có lỗi xảy ra', 'danger');
    }
  }

  async function doReply() {
    if (!replyText.trim()) return;
    try {
      await tasksApi.replyTask(task._id, replyText.trim());
      setReplyText('');
      await load();
      await onChanged();
    } catch (err) {
      toast(err.response?.data?.message || 'Có lỗi xảy ra', 'danger');
    }
  }

  async function doConfirm() {
    try {
      await tasksApi.confirmTask(task._id);
      toast('Đã xác nhận, nhân viên đã có thể thấy công việc');
      await load();
      await onChanged();
    } catch (err) {
      toast(err.response?.data?.message || 'Có lỗi xảy ra', 'danger');
    }
  }

  async function doReview(custId, newState) {
    await tasksApi.reviewItem(task._id, custId, newState, reviewNotes[custId] || '');
    await load();
    await onChanged();
  }

  async function doFinalize() {
    try {
      await tasksApi.finalizeTask(task._id);
      toast('Đã hoàn tất duyệt công việc');
      onClose();
      await onChanged();
    } catch (err) {
      toast(err.response?.data?.message || 'Có lỗi xảy ra', 'danger');
    }
  }

  async function loadStaffAndOpenAssign() {
    const own = await myStaff().catch(() => []);
    setStaff(own);
    setAssignOpen(true);
  }

  async function doAssign(userId) {
    try {
      await tasksApi.assignTask(task._id, userId);
      toast('Đã giao việc');
      setAssignOpen(false);
      await load();
      await onChanged();
    } catch (err) {
      toast(err.response?.data?.message || 'Có lỗi xảy ra', 'danger');
    }
  }

  // TP tự care KH: lưu hết kết quả đang nhập rồi kết thúc (tự duyệt Đạt) trong 1 lần.
  async function doSelfFinish() {
    const hasStatuses = task.statuses && task.statuses.length > 0;
    try {
      for (const it of task.items) {
        const k = itemKey(it);
        const note = (notes[k] || '').trim();
        if (!note) return toast('Còn KH chưa ghi kết quả xử lý', 'warn');
        if (hasStatuses && !picks[k]) return toast('Hãy chọn trạng thái kết quả cho tất cả KH', 'warn');
        if (note !== (it.note || '') || (hasStatuses && (picks[k] || '') !== (it.resultStatus || ''))) {
          await tasksApi.saveItemNote(task._id, k, note, picks[k] || '');
        }
      }
      await tasksApi.selfFinishTask(task._id);
      toast('Đã hoàn thành công việc');
      onClose();
      await onChanged();
    } catch (err) {
      toast(err.response?.data?.message || 'Có lỗi xảy ra', 'danger');
    }
  }

  return (
    <>
      <span className="close" onClick={onClose}>
        ×
      </span>
      {onBack && (
        <button className="btn ghost sm" style={{ marginBottom: 10 }} onClick={onBack}>
          ← Quay lại
        </button>
      )}
      <div className="row between" style={{ alignItems: 'flex-start' }}>
        <h3>{task.title}</h3>
        {canEdit && (
          <button className="btn ghost sm" onClick={() => setEditing(true)}>
            ✏️ Sửa yêu cầu
          </button>
        )}
      </div>
      <div className="small muted" style={{ marginBottom: 8 }}>
        {task.detail}
      </div>
      <div className="row" style={{ gap: 14, flexWrap: 'wrap', marginBottom: 12 }}>
        <span className="small">
          Nhóm: <b>{task.group?.name || '—'}</b>
        </span>
        <span className="small">
          Phụ trách: <b>{assignee ? assignee.name : 'Chưa giao'}</b>
        </span>
        <span className="small">
          Hạn NV: <b>{fmtDate(task.deadline)}</b>
        </span>
        <span className="small">
          Hạn duyệt: <b>{fmtDate(task.reviewDeadline)}</b>
        </span>
        <span className="small">
          Hoàn thành:{' '}
          <b style={{ color: comp.pct >= 80 ? 'var(--ok)' : comp.pct >= 50 ? 'var(--warn)' : 'var(--danger)' }}>
            {comp.pct}%
          </b>{' '}
          ({comp.done}/{comp.total})
        </span>
      </div>

      {task.periodMid && (
        <div className="small muted" style={{ marginBottom: 12 }}>
          Số liệu so sánh — Tuần trước: <b>{fmtDate(task.periodFrom)}</b> –{' '}
          <b>{fmtDate(new Date(new Date(task.periodMid).getTime() - 864e5))}</b> · Tuần này:{' '}
          <b>{fmtDate(task.periodMid)}</b> – <b>{fmtDate(task.periodTo)}</b>
        </div>
      )}

      {task.statuses && task.statuses.length > 0 && <StatusBreakdown task={task} />}

      <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 8 }}>
        Danh sách khách hàng cần xử lý ({task.items.length})
      </div>

      {task.items.length === 0 ? (
        <div className="empty">Chưa gắn KH</div>
      ) : (
        (() => {
          const hasDeposit = task.items.some((it) => itemCust(it).ratio != null);
          const hasRevenue = task.items.some((it) => it.currRevenue != null);
          const hasStatuses = task.statuses && task.statuses.length > 0;
          const ratioColor = (r) => (r >= 100 ? 'var(--ok)' : r >= 50 ? 'var(--warn)' : 'var(--danger)');
          const stateChipOf = (it) => {
            if (it.state === 'pending') return <span className="chip todo">Chưa làm</span>;
            if (it.state === 'noted')
              return (
                <>
                  <span className="chip review">Chờ duyệt</span>
                  {it.notedLate && <span className="chip late"> Trễ</span>}
                </>
              );
            if (it.state === 'approved')
              return (
                <>
                  <span className="chip done">Đạt</span>
                  {it.notedLate && <span className="chip late"> (trễ)</span>}
                </>
              );
            if (it.state === 'rejected') return <span className="chip late">Không đạt</span>;
            return null;
          };

          return (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ fontSize: 12.5 }}>
                <thead>
                  <tr>
                    <th>Mã KH</th>
                    <th>Tên KH</th>
                    <th>Sale</th>
                    {hasDeposit && <th className="r">Tổng tiền</th>}
                    {hasDeposit && <th className="r">Đã T.Toán</th>}
                    {hasDeposit && <th className="r">Tỷ lệ cọc</th>}
                    {hasDeposit && <th className="r">Công nợ</th>}
                    {hasRevenue && <th className="r">Tuần trước</th>}
                    {hasRevenue && <th className="r">Tuần này</th>}
                    {hasStatuses && <th>Trạng thái KQ</th>}
                    <th style={{ minWidth: 220 }}>Kết quả xử lý</th>
                    <th style={{ minWidth: 180 }}>Ghi chú TP</th>
                    <th className="c">Trạng thái</th>
                    <th className="c">Thao tác</th>
                  </tr>
                </thead>
                <tbody>
                  {task.items.map((it) => {
                    const c = itemCust(it);
                    const key = itemKey(it);
                    // NV chỉ sửa khi việc đang làm (assigned/doing). Sau khi đã NỘP (review) hoặc xong -> khóa.
                    const editable =
                      (isOwnerNV || isSelfLeadTask) &&
                      ['assigned', 'doing'].includes(task.status) &&
                      ['pending', 'noted', 'rejected'].includes(it.state);
                    return (
                      <tr key={key}>
                        <td>{c.code || '—'}</td>
                        <td>{c.name}</td>
                        <td className="small">{c.sale || '—'}</td>
                        {hasDeposit && <td className="r">{c.ratio != null ? fmtVND(c.total) : '—'}</td>}
                        {hasDeposit && <td className="r">{c.ratio != null ? fmtVND(c.paid) : '—'}</td>}
                        {hasDeposit && (
                          <td className="r">
                            {c.ratio != null ? <b style={{ color: ratioColor(c.ratio) }}>{c.ratio}%</b> : '—'}
                          </td>
                        )}
                        {hasDeposit && <td className="r">{c.ratio != null ? fmtVND(c.debt) : '—'}</td>}
                        {hasRevenue && (
                          <td className="r small">
                            {it.currRevenue != null ? `${it.prevCount ?? 0} đơn · ${fmtVND(it.prevRevenue || 0)}` : '—'}
                          </td>
                        )}
                        {hasRevenue && (
                          <td className="r small">
                            {it.currRevenue != null ? `${it.currCount ?? 0} đơn · ${fmtVND(it.currRevenue || 0)}` : '—'}
                          </td>
                        )}
                        {hasStatuses && (
                          <td>
                            {editable ? (
                              <select
                                value={picks[key] || ''}
                                onChange={(e) => setPicks({ ...picks, [key]: e.target.value })}
                              >
                                <option value="">— Chọn —</option>
                                {task.statuses.map((s) => (
                                  <option key={s} value={s}>
                                    {s}
                                  </option>
                                ))}
                              </select>
                            ) : it.resultStatus ? (
                              <span className="chip doing">{it.resultStatus}</span>
                            ) : (
                              <span className="small muted">—</span>
                            )}
                          </td>
                        )}
                        <td style={{ minWidth: 220 }}>
                          {editable ? (
                            <textarea
                              rows={2}
                              placeholder="Ghi kết quả xử lý KH này..."
                              value={notes[key] || ''}
                              onChange={(e) => setNotes({ ...notes, [key]: e.target.value })}
                            />
                          ) : it.note ? (
                            it.note
                          ) : (
                            <span className="small muted">Chưa có ghi chú</span>
                          )}
                        </td>
                        <td style={{ minWidth: 180 }}>
                          {showReview ? (
                            <input
                              value={reviewNotes[key] || ''}
                              onChange={(e) => setReviewNotes({ ...reviewNotes, [key]: e.target.value })}
                              placeholder="Ghi chú khi duyệt…"
                            />
                          ) : it.reviewNote ? (
                            it.reviewNote
                          ) : (
                            <span className="small muted">—</span>
                          )}
                        </td>
                        <td className="c">{stateChipOf(it)}</td>
                        <td className="c">
                          {editable && (
                            <button className="btn sm" onClick={() => saveNote(key)}>
                              Lưu
                            </button>
                          )}
                          {showReview && it.state === 'noted' && (
                            <div className="row" style={{ gap: 4, justifyContent: 'center' }}>
                              <button className="btn sm ok" onClick={() => doReview(key, 'approved')}>
                                Đạt
                              </button>
                              <button className="btn sm danger" onClick={() => doReview(key, 'rejected')}>
                                Không
                              </button>
                            </div>
                          )}
                          {showReview && ['approved', 'rejected'].includes(it.state) && (
                            <button className="btn sm ghost" onClick={() => doReview(key, 'noted')}>
                              Đổi lại
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          );
        })()
      )}

      {assignOpen && (
        <div className="card" style={{ background: 'var(--panel2)' }}>
          <h3>Giao việc</h3>
          {!staff.length ? (
            <div className="empty">Phòng bạn chưa có nhân viên</div>
          ) : (
            <div className="field">
              <label>Chọn nhân viên trực tiếp</label>
              <select defaultValue="" onChange={(e) => e.target.value && doAssign(e.target.value)}>
                <option value="" disabled>
                  -- chọn --
                </option>
                {staff.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>
      )}

      {/* Trao đổi CSKH ↔ TP ↔ sale — hiện cho cả 3, cập nhật mỗi khi có người phản hồi. */}
      <div style={{ marginTop: 14, borderTop: '1px solid var(--border)', paddingTop: 12 }}>
        <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 8 }}>💬 Trao đổi ({(task.thread || []).length})</div>
        {(task.thread || []).length ? task.thread.map((m, i) => (
          <div key={i} style={{ marginBottom: 8 }}>
            <div className="small"><b>{m.byName || '—'}</b> <span className="muted">· {m.role} · {fmtDateTime(m.at)}</span></div>
            <div style={{ fontSize: 13, whiteSpace: 'pre-wrap' }}>{m.text}</div>
          </div>
        )) : <div className="small muted" style={{ marginBottom: 8 }}>Chưa có trao đổi.</div>}
        <div className="row" style={{ gap: 6, marginTop: 8, alignItems: 'flex-start' }}>
          <textarea rows={2} value={replyText} onChange={(e) => setReplyText(e.target.value)} placeholder="Nhập phản hồi / trao đổi…" style={{ flex: 1 }} />
          <button className="btn sm" onClick={doReply} disabled={!replyText.trim()}>Gửi</button>
        </div>
      </div>

      <div className="row" style={{ marginTop: 12, borderTop: '1px solid var(--border)', paddingTop: 12 }}>
        {canReview && task.status === 'pending_confirm' && (
          <button className="btn sm ok" onClick={doConfirm}>
            ✅ Giao việc cho nhân viên
          </button>
        )}
        {canAssign && task.status === 'pool' && (
          <button className="btn sm" onClick={loadStaffAndOpenAssign}>
            Giao nhân viên
          </button>
        )}
        {canAssign && ['assigned', 'doing', 'review'].includes(task.status) && (
          <button className="btn sm ghost" onClick={loadStaffAndOpenAssign}>
            Đổi người phụ trách
          </button>
        )}
        {isOwnerNV && ['assigned', 'doing'].includes(task.status) && (
          <button className="btn sm ok" onClick={doSubmit}>
            Nộp cho trưởng phòng
          </button>
        )}
        {isSelfLeadTask && ['assigned', 'doing'].includes(task.status) && (
          <button className="btn sm ok" onClick={doSelfFinish}>
            ✅ Kết thúc công việc
          </button>
        )}
        {canReview && ['doing', 'review'].includes(task.status) && ((task.items?.length > 0 && task.items.every((i) => i.state !== 'pending')) || (task.items?.length === 0 && task.status === 'review')) && (
          <button className="btn sm ok" onClick={doFinalize}>
            {task.items?.length === 0 ? '✅ Xác nhận hoàn tất' : 'Hoàn tất duyệt'}
          </button>
        )}
        <div className="spacer"></div>
        <button className="btn ghost" onClick={onClose}>
          Đóng
        </button>
      </div>
    </>
  );
}

function CreateTaskForm({ groups, onCancel, onSubmit }) {
  return (
    <>
      <span className="close" onClick={onCancel}>
        ×
      </span>
      <h3>Tạo công việc mới</h3>
      <div className="empty">Hãy tích chọn KH ở màn "Danh sách quét" trước khi tạo công việc.</div>
      <div className="row" style={{ marginTop: 16 }}>
        <div className="spacer"></div>
        <button className="btn" onClick={onSubmit}>
          Đã hiểu
        </button>
      </div>
    </>
  );
}

// Bảng tỷ lệ KH theo từng trạng thái — tính trên các KH ĐƯỢC DUYỆT ĐẠT.
function StatusBreakdown({ task }) {
  const total = task.items.length;
  const approved = task.items.filter((it) => it.state === 'approved');
  // đếm theo trạng thái (chỉ KH đạt)
  const counts = {};
  task.statuses.forEach((s) => (counts[s] = 0));
  let approvedNoStatus = 0;
  approved.forEach((it) => {
    if (it.resultStatus && counts[it.resultStatus] != null) counts[it.resultStatus]++;
    else approvedNoStatus++;
  });
  const pct = (n) => (total ? Math.round((n / total) * 100) : 0);

  return (
    <div className="card" style={{ background: 'var(--panel2)' }}>
      <h3 style={{ marginBottom: 10 }}>📊 Tỷ lệ KH theo trạng thái (trên KH được duyệt đạt)</h3>
      <div className="small muted" style={{ marginBottom: 10 }}>
        Đã duyệt đạt: <b>{approved.length}</b>/{total} KH
      </div>
      <table style={{ fontSize: 13 }}>
        <thead>
          <tr>
            <th>Trạng thái</th>
            <th className="c">Số KH đạt</th>
            <th className="r">Tỷ lệ</th>
          </tr>
        </thead>
        <tbody>
          {task.statuses.map((s) => (
            <tr key={s}>
              <td>{s}</td>
              <td className="c">{counts[s]}</td>
              <td className="r">
                <b style={{ color: 'var(--accent2)' }}>{pct(counts[s])}%</b>
              </td>
            </tr>
          ))}
          {approvedNoStatus > 0 && (
            <tr>
              <td className="muted">(Đạt nhưng chưa gắn trạng thái)</td>
              <td className="c">{approvedNoStatus}</td>
              <td className="r">{pct(approvedNoStatus)}%</td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

// GD sửa lại yêu cầu công việc: tiêu đề, mô tả, hạn, danh sách trạng thái kết quả.
function EditTaskForm({ task, onCancel, onSaved }) {
  const toast = useToast();
  const [title, setTitle] = useState(task.title || '');
  const [detail, setDetail] = useState(task.detail || '');
  const [deadline, setDeadline] = useState(
    task.deadline ? new Date(task.deadline).toISOString().slice(0, 10) : ''
  );
  const [useStatuses, setUseStatuses] = useState((task.statuses || []).length > 0);
  const [statuses, setStatuses] = useState(task.statuses || []);
  const [statusInput, setStatusInput] = useState('');
  const [busy, setBusy] = useState(false);

  function addStatus() {
    const v = statusInput.trim();
    if (!v || statuses.includes(v)) {
      setStatusInput('');
      return;
    }
    setStatuses([...statuses, v]);
    setStatusInput('');
  }

  async function save() {
    if (busy) return;
    if (!title.trim()) return toast('Nhập tiêu đề', 'warn');
    setBusy(true);
    try {
      await tasksApi.updateTask(task._id, {
        title,
        detail,
        deadline,
        statuses: useStatuses ? statuses : [],
      });
      toast('Đã cập nhật yêu cầu công việc');
      await onSaved();
    } catch (err) {
      toast(err.response?.data?.message || 'Có lỗi xảy ra', 'danger');
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <span className="close" onClick={onCancel}>
        ×
      </span>
      <h3>Sửa yêu cầu công việc</h3>
      <div className="field">
        <label>Tiêu đề công việc</label>
        <input value={title} onChange={(e) => setTitle(e.target.value)} />
      </div>
      <div className="field">
        <label>Yêu cầu chi tiết</label>
        <textarea rows={3} value={detail} onChange={(e) => setDetail(e.target.value)} />
      </div>
      <div className="field">
        <label>Hạn nhân viên hoàn thành</label>
        <input type="date" value={deadline} onChange={(e) => setDeadline(e.target.value)} onClick={(e) => e.currentTarget.showPicker?.()} />
      </div>
      <div className="demo-hint">Trưởng phòng có thêm 1 ngày sau hạn này để kiểm tra & duyệt. Nhóm/người phụ trách/danh sách KH không đổi ở đây.</div>

      <div className="field">
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
          <input type="checkbox" style={{ width: 'auto' }} checked={useStatuses} onChange={(e) => setUseStatuses(e.target.checked)} />
          Dùng trạng thái kết quả cho nhân viên chọn
        </label>
      </div>
      {useStatuses && (
        <div className="card" style={{ background: 'var(--panel2)' }}>
          <div className="row" style={{ gap: 8 }}>
            <input
              style={{ flex: 1 }}
              value={statusInput}
              onChange={(e) => setStatusInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addStatus())}
              placeholder="Nhập 1 trạng thái rồi bấm Thêm…"
            />
            <button className="btn sm" onClick={addStatus}>
              + Thêm
            </button>
          </div>
          <div style={{ marginTop: 10 }}>
            {statuses.length ? (
              statuses.map((s) => (
                <span className="tag" key={s} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                  {s}
                  <span style={{ cursor: 'pointer', color: 'var(--danger)', fontWeight: 700 }} onClick={() => setStatuses(statuses.filter((x) => x !== s))}>
                    ×
                  </span>
                </span>
              ))
            ) : (
              <span className="small muted">Chưa có trạng thái nào.</span>
            )}
          </div>
        </div>
      )}

      <div className="row" style={{ marginTop: 16 }}>
        <div className="spacer"></div>
        <button className="btn ghost" onClick={onCancel}>
          Hủy
        </button>
        <button className="btn" onClick={save} disabled={busy}>
          {busy ? 'Đang lưu…' : 'Lưu yêu cầu'}
        </button>
      </div>
    </>
  );
}
